import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2,
  ShieldCheck,
  Flag,
  Inbox,
  Check,
  X,
  LogOut,
  ArrowLeft,
  Utensils,
  ChevronRight,
  Crown,
  FileText,
  Pencil,
  BadgeCheck,
  Search,
  Trash2,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getAdminToken, setAdminToken } from "@/lib/session";
import { Input } from "@/components/ui/input";
import OutreachTab from "@/components/admin/OutreachTab";

const TABS = [
  { id: "reports", label: "Reports", icon: Flag },
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "claims", label: "Claims", icon: Crown },
  { id: "restaurants", label: "Restaurants", icon: Utensils },
  { id: "edits", label: "Edits", icon: Pencil },
  { id: "outreach", label: "Outreach", icon: Send },
];

export default function AdminPage() {
  const [params] = useSearchParams();
  const [token, setTokenState] = useState(
    () => params.get("token") || getAdminToken() || ""
  );
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("reports");

  // Auto-validate token on mount
  useEffect(() => {
    if (!token) return;
    api
      .get("/admin/ping", { headers: { "X-Admin-Token": token } })
      .then(() => {
        setAuthed(true);
        setAdminToken(token);
      })
      .catch(() => {
        setAuthed(false);
        setAdminToken(null);
      });
  }, [token]);

  const handleLogout = () => {
    setAdminToken(null);
    setTokenState("");
    setAuthed(false);
  };

  if (!authed) {
    return <AdminLogin onSubmit={(t) => setTokenState(t)} />;
  }

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen w-full bg-[#FDFBF7] text-[#2A3B2C]"
    >
      {/* Header */}
      <header className="border-b border-[#E5E1D8] bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[#647466] hover:text-[#2A3B2C]">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="w-9 h-9 rounded-xl bg-[#2A3B2C] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-display text-lg font-medium text-[#2A3B2C]">
                AlKoncierge Admin
              </h1>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A9A8B]">
                Moderation queue
              </p>
            </div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-xs text-[#647466] hover:text-[#D95D39]"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-3 flex items-center gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                  tab === t.id
                    ? "bg-[#2A3B2C] text-white"
                    : "bg-white text-[#647466] border border-[#E5E1D8] hover:border-[#2A3B2C] hover:text-[#2A3B2C]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {tab === "reports" && <ReportsTab token={token} />}
        {tab === "submissions" && <SubmissionsTab token={token} />}
        {tab === "claims" && <ClaimsTab token={token} />}
        {tab === "restaurants" && <RestaurantsTab token={token} />}
        {tab === "edits" && <EditsTab token={token} />}
        {tab === "outreach" && <OutreachTab token={token} />}
      </main>
    </div>
  );
}

// ---------- Login ----------
function AdminLogin({ onSubmit }) {
  const [v, setV] = useState("");
  const [loading, setLoading] = useState(false);
  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.get("/admin/ping", { headers: { "X-Admin-Token": v } });
      onSubmit(v);
    } catch (err) {
      toast.error("Invalid admin token");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      data-testid="admin-login"
      className="min-h-screen w-full flex items-center justify-center bg-[#FDFBF7] p-6"
    >
      <form
        onSubmit={handle}
        className="w-full max-w-sm bg-white rounded-3xl border border-[#E5E1D8] shadow-lg p-7"
      >
        <div className="mx-auto w-12 h-12 rounded-full bg-[#2A3B2C] flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-white" />
        </div>
        <h1 className="mt-4 font-display text-xl font-medium text-center text-[#2A3B2C]">
          Admin sign-in
        </h1>
        <p className="text-sm text-[#647466] text-center mt-1">
          Enter your admin token to triage reports and submissions.
        </p>
        <Input
          data-testid="admin-token-input"
          type="password"
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="Admin token"
          className="mt-5 border-[#E5E1D8] focus-visible:ring-[#2A3B2C] rounded-xl h-11"
        />
        <button
          type="submit"
          disabled={!v || loading}
          data-testid="admin-login-submit"
          className="mt-3 w-full h-11 rounded-full bg-[#2A3B2C] hover:bg-[#1f2b21] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}

// ---------- Reports tab ----------
function ReportsTab({ token }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await api.get("/admin/reports", {
        headers: { "X-Admin-Token": token },
        params,
      });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateReport = async (id, status, removeFromMap = false) => {
    try {
      await api.patch(
        `/admin/reports/${id}`,
        { status, remove_from_map: removeFromMap },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(
        removeFromMap
          ? "Report resolved · listing hidden from map"
          : `Report ${status}`
      );
      load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-medium text-[#2A3B2C]">
          {data?.pending ?? 0} pending of {data?.total ?? 0} total
        </h2>
        <div className="flex items-center gap-2">
          {["pending", "resolved", "dismissed", "all"].map((f) => (
            <button
              key={f}
              data-testid={`reports-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`h-8 px-3 rounded-full text-xs font-medium border ${
                filter === f
                  ? "bg-[#2A3B2C] text-white border-transparent"
                  : "bg-white border-[#E5E1D8] text-[#647466]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : !data?.items?.length ? (
        <Empty text="No reports here." />
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {data.items.map((r) => (
            <div
              key={r.report_id}
              data-testid={`report-row-${r.report_id}`}
              className="bg-white rounded-2xl border border-[#E5E1D8] p-4 flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-[#FFF3EE] flex items-center justify-center shrink-0">
                <Flag className="w-4 h-4 text-[#D95D39]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display font-medium text-[#2A3B2C] line-clamp-1">
                    {r.restaurant_name || "Unknown restaurant"}
                  </h3>
                  <StatusBadge status={r.status} />
                </div>
                <p className="text-xs text-[#8A9A8B] mt-0.5">
                  {r.reason_label}
                  {" · "}
                  {new Date(r.created_at).toLocaleString()}
                </p>
                {r.comment && (
                  <p className="mt-2 text-sm text-[#647466] italic">
                    “{r.comment}”
                  </p>
                )}
                <p className="text-[11px] text-[#8A9A8B] mt-2">
                  place_id: <code>{r.place_id}</code>
                  {r.reporter_email && <> · {r.reporter_email}</>}
                </p>
              </div>
              {r.status === "pending" && (
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`resolve-${r.report_id}`}
                    onClick={() => updateReport(r.report_id, "resolved")}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#2A3B2C] text-white text-xs font-medium hover:bg-[#1f2b21]"
                  >
                    <Check className="w-3 h-3" />
                    Resolve
                  </button>
                  <button
                    data-testid={`resolve-remove-${r.report_id}`}
                    onClick={() => {
                      if (window.confirm(`Resolve this report AND hide ${r.restaurant_name || "this listing"} from the public map?`)) {
                        updateReport(r.report_id, "resolved", true);
                      }
                    }}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#B73E1E] text-white text-xs font-medium hover:bg-[#962F12]"
                    title="Mark resolved and remove the listing from the public map"
                  >
                    <Trash2 className="w-3 h-3" />
                    Resolve + remove
                  </button>
                  <button
                    data-testid={`dismiss-${r.report_id}`}
                    onClick={() => updateReport(r.report_id, "dismissed")}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-xs font-medium hover:border-[#D95D39] hover:text-[#D95D39]"
                  >
                    <X className="w-3 h-3" />
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- Submissions tab ----------
function SubmissionsTab({ token }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await api.get("/admin/submissions", {
        headers: { "X-Admin-Token": token },
        params,
      });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateSub = async (id, status) => {
    try {
      await api.patch(
        `/admin/submissions/${id}`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Submission ${status}`);
      load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-medium text-[#2A3B2C]">
          {data?.pending ?? 0} pending of {data?.total ?? 0} total
        </h2>
        <div className="flex items-center gap-2">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button
              key={f}
              data-testid={`subs-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`h-8 px-3 rounded-full text-xs font-medium border ${
                filter === f
                  ? "bg-[#2A3B2C] text-white border-transparent"
                  : "bg-white border-[#E5E1D8] text-[#647466]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : !data?.items?.length ? (
        <Empty text="No submissions here." />
      ) : (
        <div className="space-y-3" data-testid="submissions-list">
          {data.items.map((s) => (
            <div
              key={s.submission_id}
              data-testid={`submission-row-${s.submission_id}`}
              className="bg-white rounded-2xl border border-[#E5E1D8] p-4 flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-[#FFF3EE] flex items-center justify-center shrink-0">
                <Utensils className="w-4 h-4 text-[#D95D39]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display font-medium text-[#2A3B2C] line-clamp-1">
                    {s.name}
                  </h3>
                  <StatusBadge status={s.status} />
                </div>
                <p className="text-xs text-[#8A9A8B] mt-0.5 line-clamp-1">
                  {s.address}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[#647466]">
                  {s.cuisine && (
                    <span className="px-2 py-0.5 rounded-full bg-[#F2EDE3]">
                      {s.cuisine}
                    </span>
                  )}
                  {s.halal_status && (
                    <span className="px-2 py-0.5 rounded-full bg-[#2A3B2C] text-white">
                      {s.halal_status}
                    </span>
                  )}
                  {s.is_owner && (
                    <span className="px-2 py-0.5 rounded-full border border-[#E1A624] text-[#E1A624]">
                      Owner-submitted
                    </span>
                  )}
                </div>
                {s.notes && (
                  <p className="mt-2 text-sm text-[#647466] italic">“{s.notes}”</p>
                )}
                <p className="text-[11px] text-[#8A9A8B] mt-2">
                  {s.submitter_email || "anonymous"} ·{" "}
                  {new Date(s.created_at).toLocaleString()}
                </p>
              </div>
              {s.status === "pending" && (
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`approve-${s.submission_id}`}
                    onClick={() => updateSub(s.submission_id, "approved")}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#2A3B2C] text-white text-xs font-medium hover:bg-[#1f2b21]"
                  >
                    <Check className="w-3 h-3" />
                    Approve
                  </button>
                  <button
                    data-testid={`reject-${s.submission_id}`}
                    onClick={() => updateSub(s.submission_id, "rejected")}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-xs font-medium hover:border-[#D95D39] hover:text-[#D95D39]"
                  >
                    <X className="w-3 h-3" />
                    Reject
                  </button>
                </div>
              )}
              {s.status === "approved" && (
                <div className="flex flex-col gap-1 shrink-0 items-stretch">
                  <Link
                    to={s.place_id ? `/?p=${encodeURIComponent(s.place_id)}` : "#"}
                    className="inline-flex items-center justify-center gap-0.5 h-8 px-3 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-xs hover:text-[#D95D39]"
                  >
                    View
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                  <button
                    data-testid={`revert-${s.submission_id}`}
                    onClick={() => {
                      if (window.confirm("Revert this approval back to rejected? The restaurant will lose its Halal Verified flag.")) {
                        updateSub(s.submission_id, "rejected");
                      }
                    }}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#B73E1E] text-[#B73E1E] text-xs font-medium hover:bg-[#B73E1E] hover:text-white"
                    title="Move this approved submission back to rejected"
                  >
                    <X className="w-3 h-3" />
                    Revert
                  </button>
                </div>
              )}
              {s.status === "rejected" && (
                <button
                  data-testid={`reapprove-${s.submission_id}`}
                  onClick={() => updateSub(s.submission_id, "approved")}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#2E7D5B] text-white text-xs font-medium hover:bg-[#256249] shrink-0"
                >
                  <Check className="w-3 h-3" />
                  Re-approve
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- Claims tab ----------
function ClaimsTab({ token }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending_admin_review");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await api.get("/admin/claims", {
        headers: { "X-Admin-Token": token },
        params,
      });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateClaim = async (id, status) => {
    try {
      await api.patch(
        `/admin/claims/${id}`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(
        status === "verified" ? "Claim approved" : "Claim rejected"
      );
      load();
    } catch (err) {
      toast.error("Update failed", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const viewProof = async (claim_id) => {
    try {
      const res = await api.get(`/admin/claims/${claim_id}/proof`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      // Open in new tab; revoke after a beat so the browser has time to load
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      toast.error("Could not load proof", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const FILTERS = [
    { id: "pending_admin_review", label: "pending" },
    { id: "verified", label: "approved" },
    { id: "rejected", label: "rejected" },
    { id: "all", label: "all" },
  ];

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-medium text-[#2A3B2C]">
          {data?.pending ?? 0} pending of {data?.total ?? 0} total
        </h2>
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`claims-filter-${f.label}`}
              onClick={() => setFilter(f.id)}
              className={`h-8 px-3 rounded-full text-xs font-medium border ${
                filter === f.id
                  ? "bg-[#2A3B2C] text-white border-transparent"
                  : "bg-white border-[#E5E1D8] text-[#647466]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : !data?.items?.length ? (
        <Empty text="No claims here." />
      ) : (
        <div className="space-y-3" data-testid="claims-list">
          {data.items.map((c) => (
            <div
              key={c.claim_id}
              data-testid={`claim-row-${c.claim_id}`}
              className="bg-white rounded-2xl border border-[#E5E1D8] p-4 flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-[#FFF3EE] flex items-center justify-center shrink-0">
                <Crown className="w-4 h-4 text-[#D95D39]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display font-medium text-[#2A3B2C] line-clamp-1">
                    {c.restaurant_name}
                  </h3>
                  <StatusBadge
                    status={c.status === "pending_admin_review" ? "pending" : c.status}
                  />
                </div>
                <p className="text-xs text-[#8A9A8B] mt-0.5 line-clamp-1">
                  {c.owner_name} · {c.role || "Owner"}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[#647466]">
                  {c.halal_status && (
                    <span className="px-2 py-0.5 rounded-full bg-[#2A3B2C] text-white">
                      {c.halal_status}
                      {c.halal_certification_body
                        ? ` · ${c.halal_certification_body}`
                        : ""}
                    </span>
                  )}
                  {c.halal_has_alcohol === false && (
                    <span className="px-2 py-0.5 rounded-full bg-[#F2EDE3]">
                      No alcohol
                    </span>
                  )}
                </div>
                {c.proof && (
                  <p className="mt-2 text-sm text-[#647466] italic line-clamp-2">
                    Proof: {c.proof}
                  </p>
                )}
                {c.proof_file_id && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-[#647466]">
                    <FileText className="w-3.5 h-3.5 text-[#D95D39]" />
                    <button
                      type="button"
                      data-testid={`view-proof-${c.claim_id}`}
                      onClick={() => viewProof(c.claim_id)}
                      className="font-medium text-[#2A3B2C] underline-offset-2 hover:underline"
                    >
                      View document
                    </button>
                    <span className="font-mono text-[10px] text-[#8A9A8B]">
                      sha256: {c.proof_sha256?.slice(0, 12)}…
                    </span>
                    {c.proof_size != null && (
                      <span className="text-[10px] text-[#8A9A8B]">
                        · {(c.proof_size / 1024).toFixed(0)} KB
                      </span>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-[#8A9A8B] mt-2">
                  {c.email}
                  {c.phone ? ` · ${c.phone}` : ""} ·{" "}
                  {new Date(c.created_at).toLocaleString()}
                </p>
                <p className="text-[10px] text-[#8A9A8B] mt-0.5 font-mono">
                  place_id: <code>{c.place_id}</code>
                </p>
              </div>
              {c.status === "pending_admin_review" && (
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`approve-claim-${c.claim_id}`}
                    onClick={() => updateClaim(c.claim_id, "verified")}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#2A3B2C] text-white text-xs font-medium hover:bg-[#1f2b21]"
                  >
                    <Check className="w-3 h-3" />
                    Approve
                  </button>
                  <button
                    data-testid={`reject-claim-${c.claim_id}`}
                    onClick={() => updateClaim(c.claim_id, "rejected")}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-xs font-medium hover:border-[#D95D39] hover:text-[#D95D39]"
                  >
                    <X className="w-3 h-3" />
                    Reject
                  </button>
                </div>
              )}
              {c.status === "verified" && (
                <Link
                  to={`/?p=${encodeURIComponent(c.place_id)}`}
                  className="inline-flex items-center gap-0.5 text-xs text-[#647466] hover:text-[#D95D39] shrink-0"
                >
                  View
                  <ChevronRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-[#FFF3EE] text-[#D95D39]",
    pending_admin_review: "bg-[#FFF3EE] text-[#D95D39]",
    approved: "bg-[#2A3B2C] text-white",
    verified: "bg-[#2A3B2C] text-white",
    resolved: "bg-[#2A3B2C] text-white",
    rejected: "bg-[#F2EDE3] text-[#8A9A8B]",
    dismissed: "bg-[#F2EDE3] text-[#8A9A8B]",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
        map[status] || "bg-[#F2EDE3] text-[#647466]"
      }`}
    >
      {status}
    </span>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-5 h-5 animate-spin text-[#D95D39]" />
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="text-center py-16 rounded-2xl border border-dashed border-[#E5E1D8] bg-white">
      <Inbox className="w-7 h-7 mx-auto text-[#8A9A8B]" />
      <p className="mt-2 text-sm text-[#647466]">{text}</p>
    </div>
  );
}



// ---------- Restaurants tab (verified-list + filters + bulk actions) ----------
function RestaurantsTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    halal_status: "",
    has_alcohol: "",
    has_badge: "",
    has_claim: "",
    email: "",
    phone: "",
    city: "",
  });
  const [stats, setStats] = useState(null);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/badge-stats", {
        headers: { "X-Admin-Token": token },
      });
      setStats(data);
    } catch {
      // non-fatal
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "") return;
        if (v === "true") params[k] = true;
        else if (v === "false") params[k] = false;
        else params[k] = v;
      });
      const { data } = await api.get("/admin/restaurants", {
        params,
        headers: { "X-Admin-Token": token },
      });
      setItems(data);
      setSelected(new Set());
    } catch (err) {
      toast.error("Failed to load restaurants", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelect = (pid) => {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid);
    else next.add(pid);
    setSelected(next);
  };
  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.place_id)));
  };

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    const messages = {
      delete: `Permanently delete ${selected.size} restaurant(s)? Sponsorship history is kept.`,
      grant_badge: `Grant Halal Verified badge to ${selected.size} restaurant(s)? (1-year expiry, no charge)`,
      revoke_badge: `Revoke Halal Verified badge from ${selected.size} restaurant(s)?`,
      revoke_verification: `Remove the Halal Verified status from ${selected.size} restaurant(s)? They stay on the map.`,
      hide_from_map: `Hide ${selected.size} restaurant(s) from search + details? Reversible via Unhide.`,
      unhide_from_map: `Unhide ${selected.size} restaurant(s)?`,
    };
    if (!window.confirm(messages[action] || `Run ${action}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        "/admin/restaurants/bulk",
        { action, place_ids: Array.from(selected) },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Bulk ${action} done`, { description: `Affected: ${data.affected}` });
      await load();
      await loadStats();
    } catch (err) {
      toast.error("Bulk failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-restaurants-tab">
      {/* Badge stats strip */}
      {stats && (
        <div
          data-testid="badge-stats-strip"
          className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4"
        >
          <StatCard label="Active badges" value={stats.active_badges} tone="green" />
          <StatCard
            label="Expiring in 30 days"
            value={stats.expiring_in_30_days}
            tone={stats.expiring_in_30_days > 0 ? "orange" : "neutral"}
          />
          <StatCard
            label="Revenue (lifetime)"
            value={`$${stats.revenue_total_usd.toLocaleString()}`}
            sub={`$${stats.revenue_last_30d_usd} last 30d`}
          />
          <StatCard
            label="Upcoming renewal MRR"
            value={`$${stats.upcoming_renewal_mrr_usd}`}
            sub={`Projection: $5 × ${stats.expiring_in_30_days} expiring`}
          />
        </div>
      )}
      {/* Filters */}
      <div className="rounded-2xl border border-[#E5E1D8] bg-white p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          <SelectFilter
            testId="filter-halal-status"
            label="Halal status"
            value={filters.halal_status}
            onChange={(v) => setFilters((f) => ({ ...f, halal_status: v }))}
            options={[
              { v: "", l: "Any" },
              { v: "certified", l: "Certified" },
              { v: "self_declared", l: "Self-declared" },
              { v: "muslim_owned", l: "Muslim-owned" },
            ]}
          />
          <SelectFilter
            testId="filter-has-alcohol"
            label="Alcohol"
            value={filters.has_alcohol}
            onChange={(v) => setFilters((f) => ({ ...f, has_alcohol: v }))}
            options={[
              { v: "", l: "Any" },
              { v: "false", l: "No alcohol" },
              { v: "true", l: "Serves alcohol" },
            ]}
          />
          <SelectFilter
            testId="filter-has-badge"
            label="Verified badge"
            value={filters.has_badge}
            onChange={(v) => setFilters((f) => ({ ...f, has_badge: v }))}
            options={[
              { v: "", l: "Any" },
              { v: "true", l: "Has badge" },
              { v: "false", l: "No badge" },
            ]}
          />
          <SelectFilter
            testId="filter-has-claim"
            label="Claim"
            value={filters.has_claim}
            onChange={(v) => setFilters((f) => ({ ...f, has_claim: v }))}
            options={[
              { v: "", l: "Any" },
              { v: "true", l: "Claimed" },
              { v: "false", l: "Unclaimed" },
            ]}
          />
          <Input
            data-testid="filter-email"
            value={filters.email}
            onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))}
            placeholder="Owner email contains..."
            className="h-10 rounded-lg border-[#E5E1D8]"
          />
          <Input
            data-testid="filter-phone"
            value={filters.phone}
            onChange={(e) => setFilters((f) => ({ ...f, phone: e.target.value }))}
            placeholder="Phone contains..."
            className="h-10 rounded-lg border-[#E5E1D8]"
          />
          <Input
            data-testid="filter-city"
            value={filters.city}
            onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
            placeholder="City contains..."
            className="h-10 rounded-lg border-[#E5E1D8]"
          />
          <button
            data-testid="filter-apply"
            onClick={load}
            disabled={loading}
            className="h-10 rounded-lg bg-[#2A3B2C] text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <Search className="w-3.5 h-3.5" />
            Apply filters
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-bar"
          className="rounded-2xl border border-[#2A3B2C] bg-[#2A3B2C] text-white p-3 mb-3 flex items-center justify-between"
        >
          <span className="text-xs font-medium">
            {selected.size} selected
          </span>
          <div className="flex gap-2 flex-wrap justify-end">
            <button
              data-testid="bulk-grant-badge"
              onClick={() => runBulk("grant_badge")}
              disabled={busy}
              className="h-8 px-3 rounded-full bg-[#2E7D5B] hover:bg-[#256249] text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
            >
              <BadgeCheck className="w-3 h-3" />
              Grant badge
            </button>
            <button
              data-testid="bulk-revoke-badge"
              onClick={() => runBulk("revoke_badge")}
              disabled={busy}
              className="h-8 px-3 rounded-full bg-white text-[#2A3B2C] text-xs font-medium disabled:opacity-50"
            >
              Revoke badge
            </button>
            <button
              data-testid="bulk-revoke-verification"
              onClick={() => runBulk("revoke_verification")}
              disabled={busy}
              className="h-8 px-3 rounded-full bg-white text-[#2A3B2C] text-xs font-medium disabled:opacity-50"
              title="Remove the Halal Verified flag (the listing stays on the map)"
            >
              Remove halal verification
            </button>
            <button
              data-testid="bulk-hide"
              onClick={() => runBulk("hide_from_map")}
              disabled={busy}
              className="h-8 px-3 rounded-full bg-[#E1A624] hover:bg-[#C18E1B] text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
              title="Hide these listings from public search + details"
            >
              Hide from map
            </button>
            <button
              data-testid="bulk-unhide"
              onClick={() => runBulk("unhide_from_map")}
              disabled={busy}
              className="h-8 px-3 rounded-full bg-white text-[#2A3B2C] text-xs font-medium disabled:opacity-50"
            >
              Unhide
            </button>
            <button
              data-testid="bulk-delete"
              onClick={() => runBulk("delete")}
              disabled={busy}
              className="h-8 px-3 rounded-full bg-[#B73E1E] hover:bg-[#962F12] text-white text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3" />
              Delete
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty text="No restaurants matched these filters." />
      ) : (
        <div className="rounded-2xl border border-[#E5E1D8] bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F2EDE3] text-[10px] uppercase tracking-[0.1em] text-[#647466]">
                <tr>
                  <th className="p-3 text-left">
                    <input
                      data-testid="select-all"
                      type="checkbox"
                      checked={selected.size > 0 && selected.size === items.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-3 text-left">Restaurant</th>
                  <th className="p-3 text-left">City</th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Phone</th>
                  <th className="p-3 text-left">Halal</th>
                  <th className="p-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.place_id}
                    data-testid={`admin-rest-row-${r.place_id}`}
                    className="border-t border-[#E5E1D8]"
                  >
                    <td className="p-3">
                      <input
                        data-testid={`select-${r.place_id}`}
                        type="checkbox"
                        checked={selected.has(r.place_id)}
                        onChange={() => toggleSelect(r.place_id)}
                      />
                    </td>
                    <td className="p-3 max-w-[200px]">
                      <div className="font-medium text-[#2A3B2C] line-clamp-1">
                        {r.restaurant_name || "—"}
                      </div>
                      <div className="text-[10px] text-[#8A9A8B] line-clamp-1">
                        {r.address || r.place_id}
                      </div>
                    </td>
                    <td className="p-3 text-[#647466]">{r.city || "—"}</td>
                    <td className="p-3 text-[#647466] text-xs">{r.owner_email || "—"}</td>
                    <td className="p-3 text-[#647466] text-xs">{r.phone || "—"}</td>
                    <td className="p-3 text-xs">
                      {r.halal_status ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#F2EDE3] text-[#2A3B2C] font-medium">
                          {r.halal_status.replace("_", " ")}
                        </span>
                      ) : (
                        <span className="text-[#8A9A8B]">—</span>
                      )}
                      {r.halal_has_alcohol === true && (
                        <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#FFE0D6] text-[#B73E1E]">
                          ALC
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {r.has_badge && r.badge_expires_at ? (
                        <BadgeExpiryCell
                          expiresAt={r.badge_expires_at}
                          daysRemaining={r.badge_days_remaining}
                          grantedByAdmin={r.badge_granted_by_admin}
                        />
                      ) : (
                        <span className="text-[#8A9A8B]">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {r.has_badge && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#2E7D5B] text-white">
                          <BadgeCheck className="w-2.5 h-2.5" />
                          Badge
                        </span>
                      )}
                      {r.has_sponsorship && (
                        <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-[#E1A624] to-[#D95D39] text-white">
                          <Crown className="w-2.5 h-2.5" />
                          Featured
                        </span>
                      )}
                      {r.is_hidden && (
                        <span
                          data-testid={`hidden-${r.place_id}`}
                          className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#B73E1E] text-white"
                          title="Hidden from public map (admin)"
                        >
                          HIDDEN
                        </span>
                      )}
                      {r.has_claim && (
                        <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#2A3B2C] text-white">
                          Claimed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SelectFilter({ testId, label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#647466] block mb-1">
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-lg border border-[#E5E1D8] px-3 text-sm bg-white"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  );
}

function BadgeExpiryCell({ expiresAt, daysRemaining, grantedByAdmin }) {
  const dueSoon = daysRemaining !== null && daysRemaining !== undefined && daysRemaining <= 30;
  const formatted = (() => {
    try {
      return new Date(expiresAt).toLocaleDateString();
    } catch {
      return "—";
    }
  })();
  return (
    <div className="flex flex-col">
      <span className={dueSoon ? "text-[#D95D39] font-medium" : "text-[#2A3B2C]"}>
        {formatted}
      </span>
      <span className="text-[9px] text-[#8A9A8B]">
        {daysRemaining !== null && daysRemaining !== undefined
          ? `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`
          : ""}
        {grantedByAdmin && (
          <span className="ml-1 px-1 py-0.5 rounded text-[8px] font-bold bg-[#F2EDE3] text-[#647466]">
            ADMIN
          </span>
        )}
      </span>
    </div>
  );
}

function StatCard({ label, value, sub, tone }) {
  const toneCls =
    tone === "green"
      ? "bg-[#2E7D5B] text-white"
      : tone === "orange"
      ? "bg-[#FFF3EE] text-[#D95D39] border border-[#F2D7C8]"
      : "bg-white border border-[#E5E1D8] text-[#2A3B2C]";
  return (
    <div className={`rounded-2xl p-3 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">
        {label}
      </div>
      <div className="font-display text-2xl mt-1 leading-tight">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

// ---------- Edits tab (review queue) ----------
function EditsTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/edits", {
        params: { status: "pending" },
        headers: { "X-Admin-Token": token },
      });
      setItems(data);
    } catch (err) {
      toast.error("Failed to load edits", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (edit, action) => {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt("Reason for rejection (optional):") || null;
    }
    setBusyId(edit.edit_id);
    try {
      await api.patch(
        `/admin/edits/${edit.edit_id}`,
        { action, reason },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Edit ${action}d`);
      await load();
    } catch (err) {
      toast.error("Decision failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Loader />;
  if (items.length === 0) return <Empty text="No edits waiting for review." />;

  return (
    <div data-testid="admin-edits-tab" className="space-y-3">
      {items.map((e) => (
        <div
          key={e.edit_id}
          data-testid={`edit-row-${e.edit_id}`}
          className="rounded-2xl border border-[#E5E1D8] bg-white p-4"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="font-display text-lg text-[#2A3B2C]">
                {e.restaurant_name || e.place_id}
              </div>
              <div className="text-xs text-[#647466]">
                {e.owner_email} ·{" "}
                {new Date(e.created_at).toLocaleString()}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                data-testid={`approve-${e.edit_id}`}
                onClick={() => decide(e, "approve")}
                disabled={busyId === e.edit_id}
                className="h-9 px-3 rounded-full bg-[#2E7D5B] hover:bg-[#256249] text-white text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Check className="w-3 h-3" />
                Approve
              </button>
              <button
                data-testid={`reject-${e.edit_id}`}
                onClick={() => decide(e, "reject")}
                disabled={busyId === e.edit_id}
                className="h-9 px-3 rounded-full bg-white border border-[#E5E1D8] text-[#647466] text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
              >
                <X className="w-3 h-3" />
                Reject
              </button>
            </div>
          </div>

          {Object.keys(e.fields || {}).length > 0 && (
            <div className="rounded-lg bg-[#F2EDE3] p-3 space-y-1">
              {Object.entries(e.fields).map(([k, v]) => (
                <div key={k} className="text-xs flex gap-2">
                  <span className="text-[#8A9A8B] uppercase tracking-wider w-32 shrink-0">
                    {k}
                  </span>
                  <span className="text-[#2A3B2C]">
                    {typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {(e.pending_photos.length > 0 || e.pending_menus.length > 0) && (
            <div className="mt-3 space-y-2">
              {e.pending_photos.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#647466] mb-1">
                    Photos ({e.pending_photos.length})
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {e.pending_photos.map((p) => (
                      <AdminAttachment key={p.file_id} kind="photo" file={p} token={token} />
                    ))}
                  </div>
                </div>
              )}
              {e.pending_menus.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#647466] mb-1">
                    Menus ({e.pending_menus.length})
                  </div>
                  <ul className="space-y-1.5">
                    {e.pending_menus.map((m) => (
                      <li key={m.file_id}>
                        <AdminAttachment kind="menu" file={m} token={token} asLink />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AdminAttachment({ kind, file, token, asLink }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const path = kind === "photo" ? "edits/photo" : "edits/menu";
    api
      .get(`/admin/${path}/${file.file_id}`, {
        responseType: "blob",
        headers: { "X-Admin-Token": token },
      })
      .then((res) => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(res.data);
        setUrl(blobUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.file_id]);

  if (kind === "photo") {
    return (
      <div className="aspect-square rounded-md overflow-hidden bg-[#F2EDE3] border border-[#E5E1D8]">
        {url ? (
          <img src={url} alt={file.filename} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-3 h-3 animate-spin text-[#8A9A8B]" />
          </div>
        )}
      </div>
    );
  }
  if (asLink) {
    return (
      <a
        href={url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between gap-2 rounded-lg border border-[#E5E1D8] px-3 py-2 text-xs text-[#2A3B2C] hover:border-[#D95D39]"
      >
        <span className="truncate">{file.filename}</span>
        <span className="text-[#D95D39]">Open</span>
      </a>
    );
  }
  return null;
}
